import { useContext } from 'react';
import { useCartId } from '../components/Cart/CartIdContext';
import { getArticleNumber } from '../components/Mutation/AddToCart/addToCartUtils';
import ProductConfigurationContext from '../components/ProductConfigurationProvider/ProductConfigurationContext';
import { useDynamicPrice } from '../hooks/useDynamicPrice';
import { useFormik } from 'formik';
import { useIntl } from '@jetshop/intl';
import { useStockStatus } from '../hooks/Product/useStockStatus';
const useValidate = ({ commentValidators, articleNumber, product, variant, getMissingOptions }) => {
    var _a, _b;
    const t = useIntl();
    const { status, maxOrderQuantity } = useStockStatus(variant || product);
    const isPreOrderRequired = !!((_a = product.preOrder) === null || _a === void 0 ? void 0 : _a.fromDate) && !!((_b = product.preOrder) === null || _b === void 0 ? void 0 : _b.toDate);
    const validate = (values) => {
        const errors = {};
        if (status !== 'inStock') {
            errors.status = t('Product is not in stock');
        }
        if (values.quantity < 1) {
            errors.quantity = t('Please enter a valid quantity');
        }
        if (maxOrderQuantity && values.quantity > maxOrderQuantity) {
            errors.quantity = t('{maxOrderQuantity} is the maximum order quantity', {
                maxOrderQuantity
            });
        }
        Object.keys(values.comments).forEach((name) => {
            var _a;
            const validator = commentValidators[name];
            if (validator) {
                const error = validator({ value: values.comments[name] });
                if (error) {
                    if (typeof errors.comments === 'undefined') {
                        errors.comments = {};
                    }
                    errors.comments[name] = error;
                }
            }
            if (((_a = product.customerComments.find((customerComment) => customerComment.name === name)) === null || _a === void 0 ? void 0 : _a.required) &&
                values.comments[name].length === 0) {
                if (typeof errors.comments === 'undefined') {
                    errors.comments = {};
                }
                errors.comments[name] = t(`{customerCommentName} is required`, {
                    customerCommentName: name
                });
            }
        });
        if (!articleNumber) {
            if (typeof errors.options === 'undefined') {
                errors.options = {};
            }
            const missingOptions = getMissingOptions();
            missingOptions.forEach((option) => {
                errors.options[option.name] = t(`{optionName} is required`, {
                    optionName: option.name
                });
            });
        }
        if (!values.preOrder && isPreOrderRequired) {
            errors.preOrder = t('Pre-order date is required');
        }
        return errors;
    };
    return { validate };
};
export function useAddToCart({ product, variant, submit, commentValidators = {}, getMissingOptions, initialValues }) {
    var _a, _b;
    if (!product)
        throw new Error('No product was supplied to useAddToCart');
    const productConfigurationContext = useContext(ProductConfigurationContext);
    const configurationIds = productConfigurationContext === null || productConfigurationContext === void 0 ? void 0 : productConfigurationContext.ids;
    const price = useDynamicPrice(product, variant);
    const { cartId } = useCartId();
    const articleNumber = getArticleNumber(product, variant);
    const initialComments = {};
    if (product.customerComments) {
        product.customerComments.forEach(({ name }) => (initialComments[name] = ''));
    }
    const initialOptions = {};
    (_b = (_a = product === null || product === void 0 ? void 0 : product.variants) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.forEach((option) => {
        initialOptions[option.name] = '';
    });
    const { validate } = useValidate({
        commentValidators,
        articleNumber,
        product,
        variant,
        getMissingOptions
    });
    const formik = useFormik({
        validateOnChange: false,
        validate,
        initialValues: Object.assign({ quantity: 1, comments: initialComments, options: initialOptions }, initialValues),
        onSubmit: (values) => {
            const comments = Object.keys(values.comments).map((name) => ({
                name,
                value: values.comments[name]
            }));
            const preOrderDate = values.preOrder ? new Date(values.preOrder) : null;
            submit({
                input: {
                    quantity: values.quantity,
                    preOrderDate,
                    comments,
                    articleNumber,
                    cartId,
                    configurationIds
                },
                optimistic: Object.assign(Object.assign({}, price), { quantity: values.quantity, product,
                    variant })
            });
        }
    });
    return {
        form: formik
    };
}
//# sourceMappingURL=useAddToCart.js.map